/**
 * AdminAlertBell — Polls /comm/admin-alerts every 60s. Shows unread count.
 * When new high-severity alerts appear, fires browser Notification API + toast.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bell, Loader2, AlertTriangle, BellOff } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '@/lib/api';

const sevStyle = {
  high: 'bg-rose-600 text-white',
  medium: 'bg-amber-500 text-white',
  low: 'bg-slate-400 text-white',
};
const typeStyle = {
  scheduler: 'bg-purple-100 text-purple-800',
};

export const AdminAlertBell = ({ tenantId }) => {
  const qc = useQueryClient();
  const [permission, setPermission] = useState(() => (typeof Notification !== 'undefined' ? Notification.permission : 'default'));
  const seenFpsRef = useRef(new Set());

  const { data, isLoading } = useQuery({
    queryKey: ['admin-alerts-bell', tenantId],
    queryFn: () => api.get(`/api/school/tenants/${tenantId}/comm/admin-alerts`).then(r => r.data),
    refetchInterval: 60000,
    enabled: !!tenantId,
  });

  const markSeenMut = useMutation({
    mutationFn: (fingerprints) => api.post(`/api/school/tenants/${tenantId}/comm/admin-alerts/mark-seen`, { fingerprints }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-alerts-bell', tenantId] }),
  });

  // Fire browser notifications + toast for NEW high-severity alerts
  useEffect(() => {
    if (!data?.alerts) return;
    const newHigh = data.alerts.filter(a => a.unread && a.severity === 'high' && !seenFpsRef.current.has(a.fingerprint));
    if (newHigh.length > 0) {
      newHigh.forEach(a => {
        seenFpsRef.current.add(a.fingerprint);
        // Toast
        toast.warning(`${a.name} — ${a.summary}`, { duration: 8000, icon: '🚨' });
        // Browser Notification (if permitted)
        if (permission === 'granted' && typeof Notification !== 'undefined') {
          try {
            new Notification(`🚨 ${a.name}`, { body: a.summary, tag: a.fingerprint, icon: '/favicon.ico' });
          } catch {}
        }
      });
    }
  }, [data, permission]);

  const enableNotifs = async () => {
    if (typeof Notification === 'undefined') { toast.error('Notifications not supported'); return; }
    try {
      const p = await Notification.requestPermission();
      setPermission(p);
      if (p === 'granted') toast.success('Live alerts enabled');
      else toast.info('Notifications blocked');
    } catch (e) { toast.error('Could not request permission'); }
  };

  const markAllSeen = () => {
    const fps = (data?.alerts || []).filter(a => a.unread).map(a => a.fingerprint);
    if (fps.length) markSeenMut.mutate(fps);
  };

  const unread = data?.unread_count || 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="admin-bell-trigger">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center px-1" data-testid="admin-bell-count">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-full max-w-[380px] p-0" data-testid="admin-bell-popover">
        <div className="border-b p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-rose-500" />
            <span className="font-medium text-sm">Risk Alerts</span>
            {unread > 0 && <Badge variant="destructive">{unread}</Badge>}
          </div>
          <div className="flex items-center gap-1">
            {permission !== 'granted' && (
              <Button size="sm" variant="outline" onClick={enableNotifs} data-testid="admin-bell-enable-push" className="text-[11px] h-7">
                {permission === 'denied' ? <><BellOff className="h-3 w-3 mr-1" /> Blocked</> : <><Bell className="h-3 w-3 mr-1" /> Enable Live</>}
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={markAllSeen} disabled={!unread || markSeenMut.isPending} data-testid="admin-bell-mark-all">
              {markSeenMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Mark all seen'}
            </Button>
          </div>
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          {isLoading ? <div className="p-4 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div> :
           (data?.alerts || []).length === 0 ? <p className="text-sm text-slate-500 p-6 text-center" data-testid="admin-bell-empty">✓ No alerts</p> :
           <div>
             {(data?.alerts || []).slice(0, 25).map((a, i) => (
               <div key={a.id || `${a.type || 'alert'}-${a.name || ''}-${i}`} className={`p-3 border-b hover:bg-slate-50 ${a.unread ? 'bg-rose-50/30' : ''}`} data-testid={`admin-bell-alert-${a.id || i}`}>
                 <div className="flex items-center gap-2 mb-1">
                   <Badge className={sevStyle[a.severity] || ''}>{a.severity}</Badge>
                   <Badge variant="outline" className="text-[10px]">{a.type?.replace('_', ' ')}</Badge>
                   {a.unread && <span className="h-2 w-2 rounded-full bg-rose-500 ml-auto" />}
                 </div>
                 <div className="text-sm font-medium">{a.name || '—'}</div>
                 <div className="text-xs text-slate-600">{a.summary}</div>
               </div>
             ))}
           </div>}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default AdminAlertBell;
